import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, IconButton, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";

import { STATUS_BADGE } from "@/theme/tokens";
import { TOUCH_TARGET_SX } from "../touchTarget";

type Props = {
	title: ReactNode;
	onClose?: () => void;
	endingContent?: ReactNode;
	showDivider?: boolean;
	titleSx?: SxProps<Theme>;
};

/**
 * CommonHeader component to display a header with a title, optional ending content, and an optional close button.
 * @param endingContent
 * @param showDivider
 * @param onClose
 * @param title
 * @param titleSx
 */
export const CommonHeader: FC<Props> = ({ endingContent, showDivider = true, onClose, title, titleSx }) => {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
				gap: 1,
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					width: "100%",
					gap: 1,
				}}
			>
				<Typography
					component="div"
					sx={{
						minWidth: 0,
						overflowWrap: "anywhere",
						fontWeight: 600,
						fontStyle: "SemiBold",
						fontSize: "20px",
						lineHeight: "100%",
						color: STATUS_BADGE.BRAND_FALLBACK,
						...titleSx,
					}}
				>
					{title}
				</Typography>

				{onClose && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							width: "fit-content",
							flexShrink: 0,
						}}
					>
						{endingContent}
						{/* `size="small"` renders 34px — fine under a mouse, under the minimum for a finger, and
						    on a fullscreen mobile sheet this is the only way out. Coarse-only and min-only, so a
						    fine pointer keeps today's density exactly. */}
						<IconButton onClick={onClose} size={"small"} sx={TOUCH_TARGET_SX}>
							<CloseIcon />
						</IconButton>
					</Box>
				)}
			</Box>
			{showDivider && <Divider sx={{ width: "100%" }} />}
		</Box>
	);
};
